import { useEffect, useMemo, useState } from 'react'
import { BigNumber, Contract, utils } from 'ethers'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { useMproTokenContractAddress } from 'state/global/hooks'
import {
  DISTRIBUTED_TOKENS_SLOT,
  MASTER_DISTRIBUTOR_ADDRESS,
  MINTING_CHAIN_ID,
  PREMINT
} from 'utils/mproToken/tokenFacts'

const TOKEN_ABI = ['function totalSupply() view returns (uint256)']
const DISTRIBUTOR_ABI = [
  'function burnRate() view returns (uint256)',
  'function getAllTokenDistribution() view returns (uint256)',
  'function distributionStartTimestamp() view returns (uint256)'
]

export interface ChainSupply {
  chainId: number
  address: string
  /** Live `totalSupply()` on that chain, in whole MPRO. */
  supply: number | null
  /** Burn applied to a transfer on that chain, in percent. */
  burnPercent: number | null
  /** Whether new MPRO is created here, or only arrives over the bridge. */
  mints: boolean
}

export interface MproTokenStats {
  loading: boolean
  chains: ChainSupply[]
  /** Sum across every chain — for an OFT that is the real circulating supply. */
  circulating: number | null
  /** Premint plus everything the distributor has ever minted. */
  everMinted: number | null
  /** Ever minted, minus what still exists. */
  burned: number | null
  /** Unlocked by the emission schedule so far. */
  unlocked: number | null
  /** Minted out of what the schedule unlocked. */
  distributed: number | null
  /** Unlocked, and still waiting to be minted by claims. */
  readyToMint: number | null
  /** When the daily emission started. */
  distributionStart: Date | null
}

const toTokens = (value: BigNumber | null): number | null =>
  value === null ? null : Number(utils.formatUnits(value, 18))

/**
 * Everything the token card states about supply, emission and burn, read from
 * chain on every visit.
 *
 * Nothing is cached or stamped by a backend on purpose: these are the numbers a
 * holder is most likely to want to check against an explorer, and a figure they
 * cannot reproduce themselves is a figure they have to take on trust.
 *
 * The one number that is not a plain contract call is the burn. Burning
 * destroys tokens, so nothing counts them; what the chain can say is how many
 * were ever created and how many still exist, and the difference is exactly
 * what was burned.
 */
export function useMproTokenStats(): MproTokenStats {
  const contracts = useMproTokenContractAddress()
  const deployments = useMemo(
    () =>
      contracts
        .filter((c) => c.isActive && c.address)
        .map((c) => ({ chainId: c.chainId, address: c.address })),
    [contracts]
  )
  const key = deployments.map((d) => `${d.chainId}:${d.address}`).join(',')

  /**
   * Seeded from the platform's contract configuration, so the addresses and
   * the chain list appear immediately. A rate-limited or slow RPC then leaves
   * the figures blank instead of hiding the whole card behind a spinner —
   * an address a holder can verify is the part they need most, and it does not
   * depend on any node answering.
   */
  const seeded = useMemo(
    () =>
      deployments.map(({ chainId, address }) => ({
        chainId,
        address,
        supply: null,
        burnPercent: null,
        mints: chainId === MINTING_CHAIN_ID
      })),
    [key]
  )

  const [stats, setStats] = useState<MproTokenStats>({
    loading: true,
    chains: [],
    circulating: null,
    everMinted: null,
    burned: null,
    unlocked: null,
    distributed: null,
    readyToMint: null,
    distributionStart: null
  })

  useEffect(() => {
    setStats((prev) => ({ ...prev, chains: seeded }))
    if (deployments.length === 0) return undefined
    let cancelled = false

    const read = async () => {
      const chains: ChainSupply[] = await Promise.all(
        deployments.map(async ({ chainId, address }) => {
          const base = { chainId, address, mints: chainId === MINTING_CHAIN_ID }
          try {
            const provider = getSimpleRpcProviderByChainId(chainId)
            const token = new Contract(address, TOKEN_ABI, provider)
            const supply = toTokens(await token.totalSupply())
            let burnPercent: number | null = null
            try {
              const distributor = new Contract(
                MASTER_DISTRIBUTOR_ADDRESS,
                DISTRIBUTOR_ABI,
                provider
              )
              // 10000 = 100%, so 500 reads as 5%.
              burnPercent = (await distributor.burnRate()).toNumber() / 100
            } catch {
              /* a chain without the distributor simply has no burn to show */
            }
            return { ...base, supply, burnPercent }
          } catch {
            return { ...base, supply: null, burnPercent: null }
          }
        })
      )

      // The emission schedule and its counter live only on the minting chain:
      // everywhere else runs the light distributor, which neither mints nor
      // schedules anything.
      let unlocked: number | null = null
      let distributed: number | null = null
      let distributionStart: Date | null = null
      try {
        const provider = getSimpleRpcProviderByChainId(MINTING_CHAIN_ID)
        const distributor = new Contract(MASTER_DISTRIBUTOR_ADDRESS, DISTRIBUTOR_ABI, provider)
        unlocked = toTokens(await distributor.getAllTokenDistribution())
        distributionStart = new Date((await distributor.distributionStartTimestamp()).toNumber() * 1000)
        const slot = await provider.getStorageAt(MASTER_DISTRIBUTOR_ADDRESS, DISTRIBUTED_TOKENS_SLOT)
        distributed = toTokens(BigNumber.from(slot))
      } catch {
        /* leave the schedule figures unknown rather than guessing them */
      }

      const supplies = chains.map((c) => c.supply)
      const circulating = supplies.every((s) => s !== null)
        ? supplies.reduce<number>((sum, s) => sum + (s as number), 0)
        : null
      const everMinted = distributed === null ? null : PREMINT + distributed
      const burned =
        everMinted === null || circulating === null ? null : Math.max(everMinted - circulating, 0)
      const readyToMint =
        unlocked === null || distributed === null ? null : Math.max(unlocked - distributed, 0)

      if (cancelled) return
      setStats({
        loading: false,
        chains,
        circulating,
        everMinted,
        burned,
        unlocked,
        distributed,
        readyToMint,
        distributionStart
      })
    }

    void read()
    return () => {
      cancelled = true
    }
  }, [key])

  return stats
}
